import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import {
  fetchProfitabilityTrucks,
  ProfitabilityTruck,
} from "../../api/truckProfitabilityService";
import ProfitabilityForecast from "../../components/Fleet/ProfitabilityForecast/ProfitabilityForecast";

const filterTrucks = createFilterOptions<ProfitabilityTruck>({
  stringify: (truck) =>
    [
      truck.unitNumber,
      truck.vin,
      truck.licensePlate,
      truck.year,
      truck.make,
      truck.model,
    ].join(" "),
});
const truckLabel = (truck: ProfitabilityTruck) =>
  `${truck.unitNumber} — ${truck.year} ${truck.make} ${truck.model}${truck.isActive ? "" : " (inactive)"}`;

export default function ProfitabilityForecastPage() {
  const { token } = useAuth();
  const [truck, setTruck] = useState<ProfitabilityTruck | null>(null);
  const query = useQuery({
    queryKey: ["profitabilityTrucks"],
    queryFn: () => fetchProfitabilityTrucks(token),
    enabled: !!token,
    refetchOnWindowFocus: false,
  });

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Profitability Forecast
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Find a truck to review its monthly income, expenses and profitability,
        adjust assumptions and explore a suggested selling period.
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Autocomplete<ProfitabilityTruck>
          options={query.data ?? []}
          value={truck}
          onChange={(_, selected) => setTruck(selected)}
          getOptionLabel={truckLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={filterTrucks}
          loading={query.isLoading}
          disabled={query.isError}
          noOptionsText="No matching trucks"
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography>{truckLabel(option)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  VIN: {option.vin || "Unknown"} · Plate:{" "}
                  {option.licensePlate || "Unknown"}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Find a truck"
              helperText="Search active and inactive trucks by unit number, VIN, license plate, make or model."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {query.isLoading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {truck && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              mt: 2,
            }}
          >
            <Typography fontWeight={600}>Truck {truck.unitNumber}</Typography>
            <Chip
              size="small"
              label={truck.status || (truck.isActive ? "Active" : "Inactive")}
            />
            <Typography variant="body2" color="text.secondary">
              VIN: {truck.vin || "Unknown"}
            </Typography>
          </Box>
        )}
      </Paper>
      {query.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        >
          Trucks could not be loaded. Retry to find a truck and view its
          forecast.
        </Alert>
      )}
      {query.isSuccess && !query.data.length && (
        <Alert severity="info">No trucks are available.</Alert>
      )}
      {!truck &&
        !query.isError &&
        (!query.isSuccess || query.data.length > 0) && (
          <Alert severity="info">
            Select a truck to view its profitability forecast.
          </Alert>
        )}
      {truck && <ProfitabilityForecast key={truck.id} truckId={truck.id} />}
    </Box>
  );
}
